import React from "react";
import {
  Paper,
  Box,
  Typography,
  Chip,
  Link,
  useTheme,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  getAudienceCircleChipBackground,
  getNoticeFeedTypeChipBackground,
  getNoticeFeedTypeChipLabel,
  getNoticeFeedUrgentChipBackground,
  resolveNoticeAudienceCircleLabel,
  resolveNoticeFeedTypeChip,
  type NoticeStoredType,
} from "../utils/noticeFeedChips";
import { Subcircle } from "../hooks/useSubcircles";
import { useAuth } from "../context/AuthContext";
import { lightTheme } from "../styles/theme";
import { normalizeViewCount } from "../utils/viewCount";
import { noticeHasVideoProcessing, isVideoAttachmentProcessing } from "../utils/videoAttachment";
import { isDriveVideoFile } from "../utils/driveFolder";
import { VideoProcessingChip } from "./notices/VideoProcessingChip";
import NoticeCardViewStats from "./notices/NoticeCardViewStats";
import NoticeCardMedia from "./notices/NoticeCardMedia";
import {
  formatNoticeCardDate,
  getAttachmentDisplayName,
  hasMeaningfulNoticeBody,
  truncateNoticeBody,
} from "../utils/noticeCardDisplay";
import NoticeBodyHtml from "./notices/NoticeBodyHtml";
import { htmlToPlainText } from "../utils/noticeBodyHtml";
import {
  formatScheduleWhen,
  isUpcomingScheduledNotice,
  resolveNoticeFeedCreatedAt,
  isStaffOnlyNotice,
  INTERNAL_TEAM_ONLY_LABEL,
} from "../utils/noticeScheduling";

type Attachment = {
  name: string;
  url: string;
  type: string;
};

type Props = {
  title: string;
  body: string;
  createdAt: string;
  sender: string;
  type: NoticeStoredType;
  isUrgent?: boolean;
  isDraft?: boolean;
  publishStatus?: "draft" | "scheduled" | "published";
  publishAt?: string;
  staffOnly?: boolean;
  targetSubcircle?: string;
  attachments?: Attachment[];
  schoolName: string;
  viewCount?: {
    total: number;
    seen: number;
  };
  subcircles?: Subcircle[];
  variant?: "list" | "detail";
};

const chipSx = {
  fontWeight: 500,
  fontSize: 11,
  height: 22,
} as const;

const isImageAttachment = (att: Attachment) =>
  att.type?.startsWith("image/") ||
  /\.(jpe?g|png|gif|webp|heic|bmp|svg)$/i.test(att.name || att.url || "");

const isVideoAttachment = (att: Attachment) =>
  isDriveVideoFile(att.type, att.name ?? att.url);

const sameAttachment = (a: Attachment, b: Attachment) =>
  a.url === b.url && a.name === b.name;

const NoticeCard: React.FC<Props> = ({
  title,
  body,
  createdAt,
  sender,
  type,
  isUrgent,
  isDraft,
  publishStatus,
  publishAt,
  staffOnly,
  targetSubcircle,
  attachments = [],
  schoolName,
  viewCount,
  subcircles,
  variant = "list",
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const isListVariant = variant === "list";
  const [previewAttachment, setPreviewAttachment] = React.useState<Attachment | null>(
    null
  );
  const circleName = resolveNoticeAudienceCircleLabel(
    targetSubcircle,
    subcircles,
    user?.schoolId,
    user?.schoolName || schoolName
  );
  const normalizedViewCount = viewCount
    ? normalizeViewCount(viewCount)
    : null;

  const typeChip = resolveNoticeFeedTypeChip(type, attachments);
  const hasVideoProcessing = noticeHasVideoProcessing(attachments);
  const isScheduled = isUpcomingScheduledNotice({
    publishStatus,
    publishAt,
  });

  const showBody = isListVariant
    ? hasMeaningfulNoticeBody(body, title)
    : Boolean(htmlToPlainText(body || "").trim());
  const displayBody = isListVariant
    ? truncateNoticeBody(body, 200)
    : body;
  const showBodyClamp =
    isListVariant && htmlToPlainText(body || "").trim().length > 200;

  const mediaAttachments = attachments.filter(
    (att) => isImageAttachment(att) || isVideoAttachment(att)
  );
  const listAttachments = attachments.filter(
    (att) => !mediaAttachments.some((m) => sameAttachment(m, att))
  );
  const mediaThumbSize = isListVariant ? 72 : 88;
  const hasContentRow =
    mediaAttachments.length > 0 || showBody || listAttachments.length > 0;

  const isPdfAttachment = (attachment: Attachment) =>
    attachment.type === "application/pdf" ||
    attachment.url.toLowerCase().endsWith(".pdf");

  const chipsRow = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
      <Chip
        label={getNoticeFeedTypeChipLabel(typeChip)}
        size="small"
        sx={{
          ...chipSx,
          bgcolor: getNoticeFeedTypeChipBackground(theme, typeChip),
          color: lightTheme.colors.white,
        }}
      />
      {hasVideoProcessing && <VideoProcessingChip />}
      {isDraft && (
        <Chip
          label="Draft"
          size="small"
          sx={{
            ...chipSx,
            bgcolor: theme.palette.grey[600],
            color: lightTheme.colors.white,
          }}
        />
      )}
      {isScheduled && (
        <Chip
          icon={<ScheduleIcon sx={{ fontSize: "14px !important" }} />}
          label={`Scheduled · ${formatScheduleWhen(publishAt)}`}
          size="small"
          sx={{
            ...chipSx,
            bgcolor: theme.palette.warning.main,
            color: lightTheme.colors.white,
            "& .MuiChip-icon": { color: lightTheme.colors.white },
          }}
        />
      )}
      {isStaffOnlyNotice({ staffOnly }) && type === "event" && (
        <Chip
          label={INTERNAL_TEAM_ONLY_LABEL}
          size="small"
          sx={{
            ...chipSx,
            bgcolor: theme.palette.grey[700],
            color: lightTheme.colors.white,
          }}
        />
      )}
      {isUrgent && (
        <Chip
          label="Urgent"
          size="small"
          sx={{
            ...chipSx,
            bgcolor: getNoticeFeedUrgentChipBackground(theme),
            color: lightTheme.colors.white,
          }}
        />
      )}
      <Chip
        label={circleName}
        size="small"
        sx={{
          ...chipSx,
          bgcolor: getAudienceCircleChipBackground(theme, circleName),
          color: lightTheme.colors.white,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    </Box>
  );

  const titleBlock = (
    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 400,
        color: "text.primary",
        fontSize: { xs: "1.05rem", sm: "1.0625rem" },
        lineHeight: 1.3,
        mb: 0.35,
      }}
    >
      {title}
    </Typography>
  );

  const metaBlock = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.5,
        color: "text.secondary",
        fontSize: 13,
        mb:
          showBody || listAttachments.length > 0 || mediaAttachments.length > 0
            ? 0.5
            : 0,
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <PersonIcon sx={{ fontSize: 15, opacity: 0.75 }} />
        <Typography variant="body2" component="span" sx={{ fontSize: "inherit" }}>
          {sender}
        </Typography>
      </Box>
      <Typography component="span" sx={{ opacity: 0.5, fontSize: 12 }}>
        ·
      </Typography>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <CalendarTodayIcon sx={{ fontSize: 15, opacity: 0.75 }} />
        <Typography variant="body2" component="span" sx={{ fontSize: "inherit" }}>
          {formatNoticeCardDate(
            resolveNoticeFeedCreatedAt({
              createdAt,
              publishAt,
              publishStatus,
            })
          )}
        </Typography>
      </Box>
    </Box>
  );

  const bodyAndFilesBlock = (
    <>
      {showBody &&
        (isListVariant ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb:
                listAttachments.length > 0 ||
                (isListVariant && mediaAttachments.length > 0)
                  ? 0.5
                  : 0,
              fontSize: "0.875rem",
              lineHeight: 1.5,
              ...(showBodyClamp
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {displayBody}
          </Typography>
        ) : (
          <Box
            sx={{
              mb: listAttachments.length > 0 ? 0.5 : 0,
              fontSize: "0.875rem",
            }}
          >
            <NoticeBodyHtml body={body} typographyVariant="body2" />
          </Box>
        ))}
      {listAttachments.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: showBody ? 0.25 : 0 }}>
          {listAttachments.map((att, idx) => {
            const processing = isVideoAttachmentProcessing(att);
            const label = getAttachmentDisplayName(att.name);
            return (
              <Box
                key={idx}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  maxWidth: "100%",
                }}
              >
                <Chip
                  icon={<AttachFileIcon sx={{ fontSize: "14px !important" }} />}
                  label={label}
                  size="small"
                  clickable
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewAttachment(att);
                  }}
                  sx={{
                    height: 26,
                    maxWidth: 260,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    fontWeight: 500,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
                {processing && (
                  <VideoProcessingChip label="Processing video" />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );

  const mediaBlock =
    mediaAttachments.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, flexShrink: 0 }}>
        {mediaAttachments.map((att, idx) => (
          <NoticeCardMedia
            key={`${att.url}-${idx}`}
            attachment={att}
            isVideo={isVideoAttachment(att)}
            size={mediaThumbSize}
            onOpen={() => setPreviewAttachment(att)}
          />
        ))}
      </Box>
    ) : null;

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 0,
        borderRadius: 0,
        boxShadow: "none",
        bgcolor: "transparent",
        width: "100%",
      }}
    >
      {isListVariant ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "stretch" },
            gap: { xs: 1.25, md: 0 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {chipsRow}
            {titleBlock}
            {metaBlock}
            {bodyAndFilesBlock}
            {mediaBlock && (
              <Box sx={{ mt: showBody || listAttachments.length > 0 ? 0.75 : 0 }}>
                {mediaBlock}
              </Box>
            )}
          </Box>

          {normalizedViewCount && (
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: "100%", md: 132 },
                pl: { md: 2 },
                ml: { md: 2 },
                borderLeft: { md: "1px solid" },
                borderColor: { md: lightTheme.colors.borderSlateLight },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pt: { xs: 0.5, md: 0 },
                borderTop: {
                  xs: `1px solid ${lightTheme.colors.borderSlateLight}`,
                  md: "none",
                },
              }}
            >
              <NoticeCardViewStats
                viewCount={normalizedViewCount}
                layout="sidebar"
              />
            </Box>
          )}
        </Box>
      ) : (
        <>
          {chipsRow}
          {titleBlock}
          {metaBlock}
          {hasContentRow && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              {mediaBlock}
              <Box sx={{ flex: 1, minWidth: 0 }}>{bodyAndFilesBlock}</Box>
            </Box>
          )}
        </>
      )}

      {normalizedViewCount && !isListVariant && (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            Views: {normalizedViewCount.seen} of {normalizedViewCount.total} (
            {normalizedViewCount.percentage}%)
          </Typography>
        </Box>
      )}

      <Dialog
        open={Boolean(previewAttachment)}
        onClose={() => setPreviewAttachment(null)}
        fullWidth
        maxWidth="md"
      >
        {previewAttachment && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 500, minWidth: 0 }}
                noWrap
                title={previewAttachment.name}
              >
                {getAttachmentDisplayName(previewAttachment.name)}
              </Typography>
              <IconButton
                onClick={() => setPreviewAttachment(null)}
                size="small"
                aria-label="Close attachment preview"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <DialogContent sx={{ p: 2 }}>
              {isImageAttachment(previewAttachment) && (
                <Box
                  component="img"
                  src={previewAttachment.url}
                  alt={previewAttachment.name}
                  sx={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              )}

              {isPdfAttachment(previewAttachment) && (
                <Box
                  component="iframe"
                  src={previewAttachment.url}
                  title={previewAttachment.name}
                  sx={{
                    width: "100%",
                    height: { xs: "65vh", sm: "70vh" },
                    border: "none",
                    borderRadius: 1,
                  }}
                />
              )}

              {isVideoAttachment(previewAttachment) && (
                <Box
                  component="video"
                  controls
                  src={previewAttachment.url}
                  sx={{
                    width: "100%",
                    maxHeight: "70vh",
                    borderRadius: 1,
                  }}
                />
              )}

              {!isImageAttachment(previewAttachment) &&
                !isPdfAttachment(previewAttachment) &&
                !isVideoAttachment(previewAttachment) && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                      Preview is not available for this file type.
                    </Typography>
                    <Link
                      href={previewAttachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      Open file in a new tab
                    </Link>
                  </Box>
                )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default NoticeCard;
